import React from "react";
import { Machine, ProductionPhase, ProductionPhasePayload, ProductionPhaseType } from "../../types/mes";
import { t } from "../../i18n/locale";

export type PhaseRow = {
  key: string;
  phase_type_id: number | null;
  duration_min: number | null;
  machine_id: number | null;
  workers: number | null;
  temp_c: number | null;
  notes: string | null;
  /**
   * Ova faza se izvodi PARALELNO s prethodnom (obje krecu i zavrsavaju
   * istovremeno). Grupa dijeli samo trajanje; stroj, radnici, temperatura i
   * biljeske ostaju po fazi. Na bazi se grupa zapisuje kao isti `Sequence`.
   */
  parallel_with_prev: boolean;
};

let phaseRowSeq = 0;
const nextKey = () => `phase-${Date.now()}-${phaseRowSeq++}`;

/**
 * Slozi retke u paralelne grupe: uzastopni retci oznaceni `parallel_with_prev`
 * pripadaju grupi ispred sebe. Prvi redak je uvijek pocetak grupe.
 */
export const groupPhaseRows = (rows: PhaseRow[]): PhaseRow[][] => {
  const groups: PhaseRow[][] = [];
  rows.forEach((row, idx) => {
    const last = groups[groups.length - 1];
    if (last && idx > 0 && row.parallel_with_prev) last.push(row);
    else groups.push([row]);
  });
  return groups;
};

/** Map ProductionPhase rows (from the API) into editable rows. */
export const phasesToRows = (phases?: ProductionPhase[] | null): PhaseRow[] => {
  const sorted = (phases ?? [])
    .slice()
    .sort((a, b) => (a.Sequence ?? 0) - (b.Sequence ?? 0) || a.ID - b.ID);

  return sorted.map((p, idx) => {
    const prev = idx > 0 ? sorted[idx - 1] : null;
    return {
      key: nextKey(),
      phase_type_id: p.PhaseType ?? null,
      duration_min: p.DurationMin != null ? Number(p.DurationMin) : null,
      machine_id: p.Machine ?? null,
      workers: p.Workers ?? null,
      temp_c: p.TempC != null ? Number(p.TempC) : null,
      notes: p.Notes ?? null,
      parallel_with_prev:
        prev != null && p.Sequence != null && p.Sequence === prev.Sequence,
    };
  });
};

/** Serialize editable rows into the payload for PUT .../phases. */
export const rowsToPayload = (rows: PhaseRow[]): ProductionPhasePayload[] =>
  groupPhaseRows(rows.filter((r) => r.phase_type_id != null)).flatMap((group, groupIdx) =>
    group.map((r) => ({
      phase_type_id: r.phase_type_id,
      // Ista sekvenca = paralelna grupa (backend ih rasporedi u isti prozor).
      sequence: (groupIdx + 1) * 10,
      // Grupa dijeli jedno trajanje, pa se vodece upisuje na svaki clan.
      duration_min: group[0].duration_min,
      machine_id: r.machine_id,
      workers: r.workers,
      temp_c: r.temp_c,
      notes: r.notes,
    }))
  );

type Props = {
  rows: PhaseRow[];
  onChange: (rows: PhaseRow[]) => void;
  phaseTypes: ProductionPhaseType[];
  machines: Machine[];
};

const BAKING_CODE = "baking";

const PhaseEditor: React.FC<Props> = ({ rows, onChange, phaseTypes, machines }) => {
  const typeById = new Map(phaseTypes.map((t) => [t.ID, t]));
  const groups = groupPhaseRows(rows);

  /**
   * Svaka izmjena strukture grupa ide ovuda: ponovno slaganje cisti
   * `parallel_with_prev` na vodecim retcima i izjednacava trajanje unutar grupe,
   * pa te dvije invarijante ne mogu ostati razbijene nakon brisanja ili pomaka.
   */
  const commitGroups = (next: PhaseRow[][]) =>
    onChange(
      next.flatMap((group) =>
        group.map((row, idx) => ({
          ...row,
          parallel_with_prev: idx > 0,
          duration_min: group[0].duration_min,
        }))
      )
    );

  const commit = (next: PhaseRow[]) => commitGroups(groupPhaseRows(next));

  const update = (key: string, patch: Partial<PhaseRow>) => {
    onChange(rows.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  };

  const remove = (key: string) => commit(rows.filter((r) => r.key !== key));

  const moveGroup = (groupIndex: number, dir: -1 | 1) => {
    const target = groupIndex + dir;
    if (target < 0 || target >= groups.length) return;
    const copy = groups.slice();
    [copy[groupIndex], copy[target]] = [copy[target], copy[groupIndex]];
    commitGroups(copy);
  };

  const setGroupDuration = (group: PhaseRow[], duration_min: number | null) => {
    const keys = new Set(group.map((r) => r.key));
    onChange(rows.map((r) => (keys.has(r.key) ? { ...r, duration_min } : r)));
  };

  const setParallel = (key: string, parallel_with_prev: boolean) =>
    commit(rows.map((r) => (r.key === key ? { ...r, parallel_with_prev } : r)));

  const nextUnusedType = () => {
    const used = new Set(rows.map((r) => r.phase_type_id));
    const ordered = phaseTypes
      .slice()
      .sort((a, b) => (a.DefaultSequence ?? 0) - (b.DefaultSequence ?? 0));
    return ordered.find((t) => !used.has(t.ID)) ?? ordered[0];
  };

  const blankRow = (parallel_with_prev: boolean): PhaseRow => ({
    key: nextKey(),
    phase_type_id: nextUnusedType()?.ID ?? null,
    duration_min: null,
    machine_id: null,
    workers: null,
    temp_c: null,
    notes: null,
    parallel_with_prev,
  });

  const addPhase = () => commit([...rows, blankRow(false)]);

  const numOrNull = (v: string): number | null => (v === "" ? null : Number(v));

  return (
    <div className="phase-editor">
      <div className="phase-editor-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
        <label style={{ fontWeight: 600 }}>{t("Faze proizvodnje")}</label>
        <button type="button" className="btn-edit" onClick={addPhase}>
          {t("+ Dodaj fazu")}
        </button>
      </div>

      {rows.length === 0 && (
        <div className="empty-state" style={{ padding: 12 }}>
          <p>{t("Nema definiranih faza. Koristit ce se zadane vrijednosti.")}</p>
        </div>
      )}

      {rows.length > 0 && (
        <>
          <p className="phase-editor-hint">
            {t("Faze se izvode redom odozgo prema dolje. Sa \"Spoji s gornjom\" dvije faze postaju jedna paralelna grupa — krecu i zavrsavaju istovremeno i dijele jedno trajanje, a stroj, radnici i temperatura ostaju po fazi.")}
          </p>
          <div className="mes-table-wrap">
            <table className="mes-table phase-editor-table">
              <thead>
                <tr>
                  <th style={{ width: 40 }}>#</th>
                  <th style={{ width: 96 }}>{t("Izvodenje")}</th>
                  <th>{t("Faza")}</th>
                  <th style={{ width: 110 }}>{t("Trajanje (min)")}</th>
                  <th>{t("Stroj")}</th>
                  <th style={{ width: 90 }}>{t("Radnici")}</th>
                  <th style={{ width: 110 }}>{t("Temp (C)")}</th>
                  <th style={{ width: 190 }}>{t("Akcije")}</th>
                </tr>
              </thead>
              <tbody>
                {groups.flatMap((group, groupIndex) =>
                  group.map((row, rowIndex) => {
                    const type = row.phase_type_id != null ? typeById.get(row.phase_type_id) : undefined;
                    const isBaking = type?.Code === BAKING_CODE;
                    const isGroupLead = rowIndex === 0;
                    const isParallelGroup = group.length > 1;

                    return (
                      <tr
                        key={row.key}
                        className={isParallelGroup ? "phase-row-parallel" : undefined}
                      >
                        {isGroupLead && (
                          <td rowSpan={group.length}>
                            <div style={{ display: "flex", flexDirection: "column" }}>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => moveGroup(groupIndex, -1)}
                                disabled={groupIndex === 0}
                                title={t("Gore")}
                              >
                                ▲
                              </button>
                              <button
                                type="button"
                                className="btn-icon"
                                onClick={() => moveGroup(groupIndex, 1)}
                                disabled={groupIndex === groups.length - 1}
                                title={t("Dolje")}
                              >
                                ▼
                              </button>
                            </div>
                          </td>
                        )}
                        {isGroupLead && (
                          // Grupa je zajednicko stanje svih svojih faza, pa je i
                          // oznaka jedna ćelija preko cijele grupe. Po retku je
                          // izgledalo kao da je paralelna samo ona faza na kojoj
                          // je oznaka.
                          <td rowSpan={group.length} className="phase-mode-cell">
                            {isParallelGroup ? (
                              <span className="phase-parallel-pill">{t("Paralelno")}</span>
                            ) : (
                              <span className="muted">{t("Slijedno")}</span>
                            )}
                          </td>
                        )}
                        <td>
                          <select
                            value={String(row.phase_type_id ?? "")}
                            onChange={(e) => update(row.key, { phase_type_id: numOrNull(e.target.value) })}
                          >
                            <option value="">{t("Odaberi fazu")}</option>
                            {phaseTypes.map((t) => (
                              <option key={t.ID} value={t.ID}>{t.Name || t.Code}</option>
                            ))}
                          </select>
                        </td>
                        {isGroupLead && (
                          // Trajanje je jedina zajednicka informacija paralelne
                          // grupe, pa se unosi jednom za cijelu grupu.
                          <td rowSpan={group.length}>
                            <input
                              type="number"
                              value={row.duration_min ?? ""}
                              onChange={(e) => setGroupDuration(group, numOrNull(e.target.value))}
                            />
                          </td>
                        )}
                        <td>
                          {/* Every phase can carry a machine, regardless of the phase
                              type's UsesMachine flag. That flag marks which phases
                              normally have one, but bakeries do bind equipment to
                              fermentation and proofing (chambers, retarders), and gating
                              the dropdown on it left no way to record that. */}
                          <select
                            value={String(row.machine_id ?? "")}
                            onChange={(e) => update(row.key, { machine_id: numOrNull(e.target.value) })}
                          >
                            <option value="">{t("Bez stroja")}</option>
                            {machines.map((m) => (
                              <option key={m.ID} value={m.ID}>{m.MachineName}</option>
                            ))}
                          </select>
                        </td>
                        <td>
                          <input
                            type="number"
                            value={row.workers ?? ""}
                            onChange={(e) => update(row.key, { workers: numOrNull(e.target.value) })}
                          />
                        </td>
                        <td>
                          {isBaking ? (
                            <input
                              type="number"
                              value={row.temp_c ?? ""}
                              onChange={(e) => update(row.key, { temp_c: numOrNull(e.target.value) })}
                            />
                          ) : (
                            <span className="muted">-</span>
                          )}
                        </td>
                        <td>
                          <div className="phase-actions">
                            {isGroupLead
                              ? groupIndex > 0 && (
                                  <button
                                    type="button"
                                    className="btn-icon"
                                    onClick={() => setParallel(row.key, true)}
                                    title={t("Izvodi ovu fazu istovremeno s grupom iznad")}
                                  >
                                    {t("Spoji s gornjom")}
                                  </button>
                                )
                              : (
                                <button
                                  type="button"
                                  className="btn-icon"
                                  onClick={() => setParallel(row.key, false)}
                                  title={t("Izvadi fazu iz paralelne grupe")}
                                >
                                  {t("Odvoji")}
                                </button>
                              )}
                            <button type="button" className="btn-delete" onClick={() => remove(row.key)}>
                              {t("Ukloni")}
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
};

export default PhaseEditor;
